// AIchat/src/components/ChatSidebar.js
import React, { useEffect, useRef } from 'react';
import ReactDOM from 'react-dom';
import { MessageSquare, Trash2, DollarSign, Plus } from 'lucide-react';
import './ChatSidebar.css';

const ChatSidebar = ({ 
  chatHistory, 
  currentChatId, 
  onLoadChat, 
  onDeleteChat, 
  onCreateNewChat,
  isOpen,
  onToggle 
}) => {
  const sidebarRef = useRef(null);
  
  // Handle click outside to close sidebar
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (isOpen && sidebarRef.current && !sidebarRef.current.contains(event.target)) {
        const toggleBtn = event.target.closest('.history-toggle-btn');
        if (!toggleBtn && onToggle) {
          onToggle();
        }
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('touchstart', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('touchstart', handleClickOutside);
    };
  }, [isOpen, onToggle]);
  
  const handleToggleClick = (e) => {
    e.preventDefault();
    e.stopPropagation();
    console.log('History toggle clicked - calling onToggle');
    if (onToggle) {
      onToggle();
    }
  };
  const formatCost = (cost) => {
    if (cost === 0) return 'Free';
    return `$${cost.toFixed(4)}`;
  };

  const formatTokens = (tokens) => {
    if (!tokens) return '0';
    if (tokens < 1000) return tokens.toString();
    return `${(tokens / 1000).toFixed(1)}K`;
  };

  const sidebarContent = (
    <>
      <button 
        className={`history-toggle-btn ${isOpen ? 'open' : ''}`}
        onClick={handleToggleClick}
        onTouchEnd={handleToggleClick}
        title="Toggle Chat History"
        type="button"
      >
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d={isOpen ? "M15 18l-6-6 6-6" : "M9 18l6-6-6-6"}/>
        </svg>
      </button>
      
      {isOpen && <div className="sidebar-overlay" onClick={onToggle}></div>}
      <div className={`chat-sidebar ${isOpen ? 'open' : ''}`} ref={sidebarRef}>
      <div className="sidebar-header">
        <button onClick={onCreateNewChat} className="new-chat-btn">
          <Plus size={16} />
          New Chat
        </button>
      </div>
      
      <div className="sidebar-content">
        {chatHistory.length === 0 ? (
          <div className="no-chats">No previous chats</div>
        ) : (
          chatHistory.map(chat => (
            <div 
              key={chat.id} 
              className={`chat-item ${currentChatId === chat.id ? 'active' : ''}`}
              onClick={() => onLoadChat(chat.id)}
            >
              <div className="chat-item-content">
                <div className="chat-title">{chat.title}</div>
                <div className="chat-meta">
                  <span className="chat-date">
                    {new Date(chat.lastUpdated).toLocaleDateString()}
                  </span>
                  <div className="chat-stats">
                    <span className="token-count">
                      {formatTokens(chat.totalTokens)} tokens
                    </span>
                    <span className="cost-display">
                      <DollarSign size={10} />
                      {formatCost(chat.totalCost || 0)}
                    </span>
                  </div>
                </div>
                {chat.projectPath && (
                  <div className="chat-project">📁 {chat.projectPath}</div>
                )}
              </div>
              <button 
                className="delete-chat-btn"
                onClick={(e) => {
                  e.stopPropagation();
                  onDeleteChat(chat.id);
                }}
                title="Delete chat"
              >
                <Trash2 size={12} />
              </button>
            </div>
          ))
        )}
      </div>
      </div>
    </>
  );

  return ReactDOM.createPortal(sidebarContent, document.body);
};

export default ChatSidebar;